import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Alert, Button, Card, Col, Empty, Flex, Popconfirm, Row, Skeleton, Space, Tag, Typography } from 'antd';
import { FileTextOutlined, PlusOutlined } from '@ant-design/icons';
import classnames from 'classnames';
import DocumentSlotUploadModal from './DocumentSlotUploadModal';
import { filesBySlot } from './documentFiles';
import useApiClient from '../../hooks/api/useApiClient';
import useBusinessProfile from '../../hooks/context/useBusinessProfile';
import useHelpers from '../../hooks/context/useHelpers';
import usePermissions from '../../hooks/auth/usePermissions';
import { BusinessProfileReaderRoles } from '../../services/permissions/PermissionResolver';
import { DocumentHolder, DocumentSlot, LegalForm } from '../../services/exports/Constants';
import {
  useGetBusinessDocumentsQuery,
  useGetDocumentSlotsQuery,
  useRemoveBusinessDocumentMutation,
} from '../../store/api-slices';

/**
 * Get Started's six named document uploads (PROD-544), one card per slot: whether the legal form
 * requires it and whether it is still missing, the files in it, and an upload. Every file can be
 * replaced; `canRemove` also lets the person take a file back, which the backend allows until
 * onboarding is complete. Without `canUpload` the cards are read-only (sales, once onboarding is
 * done: PROD-543). The slots come from the backend, the files from the documents index,
 * which records who viewed them. Both are read per location (`currentData`), so a location switch
 * never shows the previous location's files with buttons acting on them.
 */
export default function DocumentSlots({ canRemove = false, canUpload = true }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:DocumentSlots:DocumentSlots' });
  const { formatDate } = useHelpers();

  const { company } = useSelector((state) => state.currentCompany);
  const { getValue } = useBusinessProfile();
  const { BusinessProfilesManager } = useApiClient();
  const { hasAnyRole } = usePermissions();
  const legalForm = getValue('legal_form') ?? null;
  // Store employees may not read the business profile: nothing to ask, nothing to show.
  const canRead = hasAnyRole(BusinessProfileReaderRoles);
  // A restaurant that went through the old document steps (`requires_document_slots: false`)
  // owes the slots nothing: its badge says so, and the cards must not say otherwise (Bohdan).
  const slotsRequired = company?.merchant?.requires_document_slots !== false;

  const {
    currentData: slots,
    isError: slotsFailed,
    refetch: refetchSlots,
  } = useGetDocumentSlotsQuery(
    { apiManager: BusinessProfilesManager, companyId: company?.id, legalForm },
    { skip: !canRead },
  );
  // The company goes in a header, not the request, so it is in the args for the cache key only.
  const {
    currentData: documents,
    isError: documentsFailed,
    refetch: refetchDocuments,
  } = useGetBusinessDocumentsQuery(
    { apiManager: BusinessProfilesManager, companyId: company?.id, params: {} },
    { skip: !canRead, refetchOnMountOrArgChange: true },
  );
  const [removeDocument] = useRemoveBusinessDocumentMutation();

  const files = useMemo(() => filesBySlot(documents), [documents]);

  // { slot, replacing } while the upload modal is open.
  const [upload, setUpload] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const slotLabel = (slot) => t(`slots.${slot}.label`);

  const slotHint = (slot) =>
    slot === DocumentSlot.ShareholderList && legalForm === LegalForm.Gbr
      ? t(`slots.${slot}.hint_gbr`)
      : t(`slots.${slot}.hint`);

  const remove = async (file) => {
    setRemovingId(file.id);
    const { error } = await removeDocument({ apiManager: BusinessProfilesManager, id: file.id });
    setRemovingId(null);

    if (error) {
      return toast.error(t('toasts.remove_error'));
    }

    return toast.success(t('toasts.remove_success'));
  };

  const renderTags = (slot) => (
    <Space size={4} wrap>
      <Tag color={slot.is_required ? 'blue' : 'default'}>{t(slot.is_required ? 'tags.required' : 'tags.optional')}</Tag>
      {slotsRequired && slot.is_missing && <Tag color="red">{t('tags.missing')}</Tag>}
    </Space>
  );

  const renderFileDetails = (file) => {
    const details = [
      file.kind && t(`kinds.${file.kind}`, { defaultValue: '' }),
      file.holder === DocumentHolder.Owner && t('holders.owner'),
      file.holder === DocumentHolder.Partner && t('holders.partner', { name: file.holder_name }),
      file.created_at && t('file.uploaded_at', { date: formatDate(file.created_at) }),
      file.expires_at && t('file.expires_at', { date: formatDate(file.expires_at) }),
    ].filter(Boolean);

    return (
      <Typography.Text type="secondary" className="text-xs">
        {details.join(' · ')}
      </Typography.Text>
    );
  };

  const renderFile = (slot, file) => (
    <Flex key={file.key} justify="space-between" align="start" gap={8} className="py-xs">
      <Flex vertical gap={2} className="min-w-0">
        <Flex align="center" gap={6} className="min-w-0">
          <FileTextOutlined />
          <Typography.Text ellipsis>
            {file.sides.front?.original_name ?? file.sides.back?.original_name}
          </Typography.Text>
          {slot.is_identity && file.sides.back && <Tag>{t('file.with_back')}</Tag>}
        </Flex>
        {renderFileDetails(file)}
      </Flex>
      <Space size={4}>
        {canUpload && (
          <Button size="small" onClick={() => setUpload({ slot, replacing: file })}>
            {t('buttons.replace')}
          </Button>
        )}
        {canRemove && (
          <Popconfirm
            title={t('remove_confirm.title')}
            description={t('remove_confirm.description')}
            okText={t('remove_confirm.ok')}
            cancelText={t('remove_confirm.cancel')}
            okButtonProps={{ danger: true }}
            onConfirm={() => remove(file)}
          >
            <Button size="small" danger loading={removingId === file.id}>
              {t('buttons.remove')}
            </Button>
          </Popconfirm>
        )}
      </Space>
    </Flex>
  );

  const renderSlot = (slot) => {
    const slotFiles = files[slot.slot] ?? [];

    return (
      <Col xs={24} lg={12} key={slot.slot}>
        <Card
          size="small"
          title={slotLabel(slot.slot)}
          extra={renderTags(slot)}
          className={classnames('h-full', { 'border-red-300': slotsRequired && slot.is_missing })}
          data-testid={`document-slot-${slot.slot}`}
        >
          <Typography.Paragraph type="secondary" className="text-xs">
            {slotHint(slot.slot)}
          </Typography.Paragraph>
          {slotFiles.length > 0 ? (
            slotFiles.map((file) => renderFile(slot, file))
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('empty')} className="my-xs" />
          )}
          {canUpload && (
            <Button
              icon={<PlusOutlined />}
              className="mt-sm"
              onClick={() => setUpload({ slot, replacing: null })}
              data-testid={`document-slot-${slot.slot}-add`}
            >
              {t('buttons.add')}
            </Button>
          )}
        </Card>
      </Col>
    );
  };

  if (!canRead) {
    return null;
  }

  if (slotsFailed || documentsFailed) {
    return (
      <Alert
        type="error"
        showIcon
        title={t('errors.load')}
        action={
          <Button
            size="small"
            onClick={() => {
              refetchSlots();
              refetchDocuments();
            }}
          >
            {t('buttons.retry')}
          </Button>
        }
      />
    );
  }

  if (!slots || !documents) {
    return <Skeleton active />;
  }

  return (
    <>
      <Row gutter={[16, 16]}>{slots.map(renderSlot)}</Row>
      {upload && (
        <DocumentSlotUploadModal
          slot={upload.slot}
          slotLabel={slotLabel(upload.slot.slot)}
          replacing={upload.replacing}
          onClose={() => setUpload(null)}
        />
      )}
    </>
  );
}
